import BookingItemLayout from '@/routes/account/layout/BookingItemLayout';
import TicketTripsSummary from '@/routes/account/tickets/TicketTripsSummary';
import type { BookingSearchDTO } from '@/types/dto/agent';
import { isBookingSearchItemRefunded } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { format } from '@/utils/dateTime';
import { Divider } from '@fleet/webshop-ui';
import _partition from 'lodash/partition';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TicketSummaryProps {
  bookingSearchItem: BookingSearchDTO;
  isActive: boolean;
}

const TicketSummary: FC<TicketSummaryProps> = ({
  bookingSearchItem,
  isActive,
}) => {
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  const startTime =
    bookingSearchItem.journeyOrigin?.time &&
    format(bookingSearchItem.journeyOrigin.time, 'EEE, dd MMM yyyy');
  const endTime =
    bookingSearchItem.journeyDestination?.time &&
    format(bookingSearchItem.journeyDestination.time, 'EEE, dd MMM yyyy');
  const dateRange =
    startTime && endTime && startTime !== endTime
      ? `${startTime} - ${endTime}`
      : (startTime ?? endTime);

  const [outboundTripsSummaries, inboundTripsSummaries] = _partition(
    bookingSearchItem.tripSummaries ?? [],
    (trip) => trip.direction === 'OUTBOUND'
  );

  const isRefunded = isBookingSearchItemRefunded(bookingSearchItem);

  return (
    <BookingItemLayout
      bookingSearchItem={bookingSearchItem}
      isActive={isActive}
      dateRange={dateRange}
    >
      {Boolean(outboundTripsSummaries.length) && (
        <TicketTripsSummary
          title="outboundTrips"
          tripSummaries={outboundTripsSummaries}
          isRefunded={isRefunded}
        />
      )}

      {Boolean(
        outboundTripsSummaries.length && inboundTripsSummaries.length
      ) && (
        <>
          <Divider
            orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
            className="border-neutral-light"
            grow={false}
          />
          <TicketTripsSummary
            title="returnTrips"
            tripSummaries={inboundTripsSummaries}
            isRefunded={isRefunded}
          />
        </>
      )}
    </BookingItemLayout>
  );
};

export default TicketSummary;
